/**
 * Duration and easing tokens for transitions.
 * @see cds-motion.md
 */

/** Duration tokens (ms). */
export const transitionDuration = {
  /** Skip visible animation */
  instant: 0,
  /** Micro-interactions */
  fast: 150,
  /** Standard transitions */
  normal: 225,
  /** Emphasized transitions */
  slow: 500,
} as const;

export type TransitionDurationToken = keyof typeof transitionDuration;

/** Easing tokens (CSS values). */
export const transitionEasing = {
  /** Decelerate (fast start, slow finish) — elements entering view */
  entrance: 'ease-out',
  /** Accelerate (slow start, fast finish) — elements leaving view */
  exit: 'ease-in',
  /** Smooth both ends — hover, color, general changes */
  standard: 'ease-in-out',
} as const;

export type TransitionEasingToken = keyof typeof transitionEasing;

export const defaultTimeout = {
  enter: transitionDuration.normal,
  exit: transitionDuration.fast,
} as const;

export const resizeDebounceMs = transitionDuration.fast;

const autoHeightDurationTokens = [
  transitionDuration.fast,
  transitionDuration.normal,
  transitionDuration.slow,
] as const;

/**
 * Duration (ms) for a height transition of the given pixel height.
 * Uses the same scaling formula as MUI then snaps to the nearest of
 * transitionDuration.fast, normal, or slow so auto collapse always uses a token.
 * @see https://github.com/mui/material-ui/blob/next/packages/mui-material/src/styles/createTransitions.js
 */
export function getAutoHeightDuration(height: number): number {
  if (!height) return transitionDuration.instant;
  const constant = height / 36;
  const raw = Math.round((4 + 15 * constant ** 0.25 + constant / 5) * 10);
  const clamped = Math.min(
    transitionDuration.slow,
    Math.max(transitionDuration.fast, raw)
  );
  return autoHeightDurationTokens.reduce((prev, curr) =>
    Math.abs(curr - clamped) < Math.abs(prev - clamped) ? curr : prev
  );
}
